// Move this file to correct place, This does not belong in react-ga folder

import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import {
  isMoengageObjPresent,
  trackEventGeneric,
} from '@porterin/component-library/src/ts-utils/analytics/moengage/MoEngage';
import {
  getTrackEventPayload,
  shouldFireMoeEvent,
} from '@porterin/component-library/src/ts-utils/analytics/moengage/MoEngageHelper';
import {
  publishGaEventParameter,
  publishGaEvent,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/publishers/GaPublisher';
import { CommonTrackingPayloadEvent } from '@porterin/component-library/src/ts-utils/analytics/react-ga/types/CommonTrackingPayload';
import { GaTrackingPayloadSend } from '@porterin/component-library/src/ts-utils/analytics/react-ga/types/GaTrackingPayload';
import { isDesktop } from '@porterin/lead-flow/src/components/FareEstimateHelper';
import {
  getLocaleCookie,
  getTrackingCookieSafe,
} from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { gaCookie } from '@porterin/component-library/src/ts-utils/analytics/helpers/AnalyticsManagerHelpers';
import { getUtmParams } from '@porterin/component-library/src/ts-utils/url-utils/UrlUtils';

interface MoEngageConfig {
  moengage_enable_flag: boolean;
  moengage_app_id: string;
  mo_engage_data_cluster: string;
  mo_engage_web_push_data_cluster: string;
  mo_engage_debug_logs: number;
}

export const getMoEngageConfigByLocale = (
  locale: string,
): MoEngageConfig | null => {
  const countryKey = (() => {
    switch (locale) {
      case 'bn-BD':
      case 'en-BD':
        return 'bd';
      case 'en-AE':
        return 'ae';
      case 'en-TK':
      case 'en-TR':
      case 'tr-TR':
        return 'tr';
      case 'en-IN':
        return 'in';
      default:
        return '';
    }
  })();

  if (!countryKey) {
    return null;
  }

  const appConfig = getAppConfig();
  const moengageCountryConfig = appConfig.moengage_config?.[countryKey];

  return moengageCountryConfig || null;
};

export const getGaTrackingIdByLocale = (): string => {
  switch (getLocaleCookie()) {
    case 'en-AE':
      return getAppConfig().ga4_tracking_id_ae ?? '';
    case 'en-TR':
    case 'tr-TR':
      return getAppConfig().ga4_tracking_id_tr ?? '';
    case 'en-IN':
      return getAppConfig().ga4_tracking_id_dom ?? '';
    case 'bn-BD':
    case 'en':
      return getAppConfig().ga4_tracking_id_bd ?? '';
    default:
      return '';
  }
};

export const getGaEventPayload = (
  payload: CommonTrackingPayloadEvent,
): GaTrackingPayloadSend => ({
  eventCategory: payload.category,
  eventAction: payload.action,
  eventValue: payload.value,
  eventLabel: payload.label,
  nonInteraction: payload.nonInteraction,
  hitType: 'event',
  send_to: getGaTrackingIdByLocale(),
});

export const publishEventGA = (payload: CommonTrackingPayloadEvent): void => {
  publishGaEvent(getGaEventPayload(payload));
};

export const getGa4ClientId = (): string => {
  if (gaCookie) {
    const parts = gaCookie.split('.');
    if (parts?.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  return '';
};

export const addCommonMoeAttributes = () => {
  const ga4ClientId = getGa4ClientId();
  const visitorId = getTrackingCookieSafe();
  const utmParams = getUtmParams();

  return {
    backend_service: 'platform',
    device_type: isDesktop() ? 'desktop' : 'mobile',
    client_id: ga4ClientId,
    campaign_id: utmParams.campaign_id,
    campaign: utmParams.campaign,
    source: utmParams.source,
    medium: utmParams.medium,
    term: utmParams.term,
    content: utmParams.content,
    session_id: visitorId ?? '',
  };
};

export const publishEventMoEngage = (
  payload: CommonTrackingPayloadEvent,
  locale?: string,
): void => {
  const moEngagePayload = getTrackEventPayload(payload);
  const updatedMoengagePayload = {
    ...moEngagePayload,
    ...addCommonMoeAttributes(),
  };

  // Try to get locale-specific config first, then fallback to global config
  const localeConfig = locale ? getMoEngageConfigByLocale(locale) : null;
  const enableFlag =
    localeConfig?.moengage_enable_flag ?? getAppConfig().moengage_enable_flag;

  if (
    isMoengageObjPresent() &&
    shouldFireMoeEvent(payload.action) &&
    enableFlag
  ) {
    trackEventGeneric(payload.action, updatedMoengagePayload);
  }
};

export const publishEvent = (
  payload: CommonTrackingPayloadEvent,
  locale?: string,
): void => {
  publishEventMoEngage(payload, locale);
  publishGaEvent(getGaEventPayload(payload));
};

export const publishGa4Events = (
  category: string,
  action: string,
  label?: string,
  locale?: string,
) => {
  publishEvent(
    {
      category,
      action,
      value: 0,
      label,
      nonInteraction: false,
    },
    locale,
  );
};

export const publishEvents = (
  category: string,
  action: string,
  label: string,
  locale?: string,
) => {
  publishGa4Events(category, action, label, locale);
};

export const publishEventParameterGA = <T extends Record<string, any>>(
  event_name: string,
  payload: T,
): void => {
  publishGaEventParameter(event_name, payload);
};

export const publishEventParameter = <T extends Record<string, any>>(
  event_name: string,
  payload: T,
  locale?: string,
): void => {
  const updatedMoengagePayload = {
    ...payload,
    ...addCommonMoeAttributes(),
  };

  // Try to get locale-specific config first, then fallback to global config
  const localeConfig = getMoEngageConfigByLocale(locale ?? getLocaleCookie());
  const enableFlag =
    localeConfig?.moengage_enable_flag ?? getAppConfig().moengage_enable_flag;

  if (isMoengageObjPresent() && shouldFireMoeEvent(event_name) && enableFlag) {
    trackEventGeneric(event_name, updatedMoengagePayload);
  }
  publishGaEventParameter(event_name, payload);
};

export const publishMoengageEventParameter = <T extends Record<string, any>>(
  event_name: string,
  payload: T,
  locale?: string,
): void => {
  const updatedMoengagePayload = {
    ...payload,
    ...addCommonMoeAttributes(),
  };

  // Try to get locale-specific config first, then fallback to global config
  const localeConfig = getMoEngageConfigByLocale(locale ?? getLocaleCookie());
  const enableFlag =
    localeConfig?.moengage_enable_flag ?? getAppConfig().moengage_enable_flag;

  const isMoePresent = isMoengageObjPresent();
  const shouldFire = shouldFireMoeEvent(event_name);

  if (isMoePresent && shouldFire && enableFlag) {
    trackEventGeneric(event_name, updatedMoengagePayload);
  }
};
